import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { isEqual } from 'lodash';
import md5 from 'md5';
import { GENERAL_PRESET } from '@/services/constants/report';
import { useStore } from '@/store';
import { isPageWithoutGrouping } from '@/views/reports/report-route-behavior';
import { resolveDedicatedTableMetrics } from '@/views/reports/report-metrics-bundles-static';
import { resolveAllowedMetricsForDimension } from '@/views/reports/report-columns-settings-runtime';
import {
  listDedicatedMetricSaveFlags,
  listSimpleMetricSaveFlags,
} from '@/views/reports/report-metrics-columns-runtime';
import { reportTypeFromRoute } from '@/services/utils/reportType';
import { DEFAULT_COLUMN_PRESET_STATE } from '@/views/reports/pickers/base/default-column-preset-state';
import { useMetricsPickerContext } from '@/composables/useMetricsPickerContext';
import { useColumnsSettingsDialog } from '@/composables/useColumnsSettingsDialog';
import {
  getReportColumnPlanLimits,
  tc,
  useApiFieldErrors,
  usePlanLimits,
  useReportParams,
  useTableQuery,
  useUrl,
} from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  deleteColumnSettingsPresetApiRequest,
  putColumnSettingsApiRequest,
} from '@/services/api/column-settings-api';
import {
  deleteCustomColumnApiRequest,
} from '@/services/api/custom-columns-api';
import { postColumnsGroupsSettingApiRequest } from '@/services/api/columns-groups-setting-api';
import { refreshSupportedMetricsApiRequest } from '@/services/api/api-services/report';
import { refreshCustomColumns } from '@/services/api/api-services/load-profile';

import type UiButton from '@/components/ui/ui-button.vue';

type ColumnsSettingsDialogEmit = (event: 'close') => void;

export const useColumnsSettingsDialogState = (emit: ColumnsSettingsDialogEmit) => {
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const reportParamsApi = useReportParams();
  const urlApi = useUrl();
  const { errorsDict, clearErrors, applyApiErrors } = useApiFieldErrors();
  const { hasPlanLimitsRestriction } = usePlanLimits(undefined, false);
  const columnsSettingsDialog = useColumnsSettingsDialog();

  const {
    route,
    t,
    reportMetricsName,
    groupingValuesValueList,
    existingMetrics,
    baseMetrics,
    getInheritColumns,
    findOrderBy,
    dimensionType,
    getDefaultSelected,
    tableFullDimensions,
    getCustomMetrics,
  } = useMetricsPickerContext();

  const oldSelectedMetricsHash = ref('');
  const oldGroupOfMetricsHash = ref('');
  const search = ref('');
  const selectedMetrics = ref<boolean[]>([]);
  const selectedMetricsPrevState = ref<boolean[]>([]);
  const filteredMetricsGrouped = ref<ColumnsGroup[]>([]);
  const groupingFromServer = ref<{ groups: Record<string, unknown>; columns: Record<string, unknown> }>({
    groups: {},
    columns: {},
  });
  const orderedMetrics = ref<Column[]>([]);
  const dragged = ref<string | null>(null);
  const selectAll = ref(false);
  const inputPresetName = ref('');
  const inputPresetNameNew = ref('');
  const activePreset = ref(reportParamsApi.activePreset.value);
  const isActivePresetWasUpdated = ref(false);
  const isActivePresetWasDropedToGeneral = ref(false);
  const presetSaveLoading = ref(false);
  const presetDeleteLoading = ref(false);

  const presetsRefs = ref<InstanceType<typeof UiButton>[]>([]);
  const newPreset = ref<InstanceType<typeof UiButton>>();
  const listOfGroups = ref<HTMLElement>();
  const buttons = ref<HTMLElement>();

  const canEditCustomColumns = computed(() => {
    const user = store.user;
    return !(user.isMember && user.role === 'user');
  });

  const reportGroupBy = computed(() => reportParamsApi.getReportParams()?.groupBy);

  const pagesWithoutGrouping = computed(() => isPageWithoutGrouping(route.name));

  const activeTab = computed(() => store.getActiveTab());

  const allSavedColumns = computed(() => {
    return activeTab.value
      ? reportParamsApi.getSavedColumnsForCurrentTab()
      : reportParamsApi.savedColumns.value[activePreset.value];
  });

  const savedColumns = computed(() => {
    const groupBy = reportGroupBy.value;
    const type = Array.isArray(groupBy) ? groupBy[0] : groupBy;

    return allSavedColumns.value?.[type]?.columns || [];
  });

  const isTreeView = computed(() => reportParamsApi.getReportParams()?.view === 'tree');

  const initSelectedMetrics = computed(() => {
    return savedColumns.value.reduce((acc: boolean[], column: string) => {
      const index = existingMetrics.value.findIndex(({ value }) => value === column);
      acc[index] = true;
      return acc;
    }, []);
  });

  const getAllExistingMetricsWithoutLimits = computed(() => {
    return existingMetrics.value.filter((item) => !hasPlanLimitsRestriction(getReportColumnPlanLimits(item)));
  });

  const filteredMetrics = computed((): Column[] => {
    return existingMetrics.value
      .filter((item: Column) => reportMetricsName(item.text).toLowerCase().includes(search.value.toLowerCase()))
      .map((item) => {
        return {
          ...item,
          planLimits: getReportColumnPlanLimits(item),
        };
      });
  });

  const orderedMetricsTree = computed({
    get: () => {
      const groupBy = reportGroupBy.value;
      const dimensions = store.dimensions as Record<string, { mainColumn?: string; additionalColumns?: string[] }>;

      let treeColumns: string[] = [];
      if (
        !isTreeView.value
        && urlApi.isReport.value
        && !['errors', 'postbacks', 'conversions', 'live-clicks', 'live-visits'].includes(groupBy as string)
      ) {
        const groupByArray = typeof groupBy === 'string'
          ? [groupBy]
          : (Array.isArray(groupBy) ? groupBy : []);
        treeColumns = groupByArray
          .map((key) => dimensions[key]?.mainColumn)
          .filter((key): key is string => Boolean(key));
      }
      const mainColumns = treeColumns
        .map((key) => existingMetrics.value.find(({ value }) => value === key))
        .filter((item): item is Column => Boolean(item))
        .map((item) => ({
          ...item,
          isTreeColumn: true,
        }));

      const restColumns = orderedMetrics.value?.filter((item) => item && !treeColumns.includes(item.value)) || [];

      return [...mainColumns, ...restColumns];
    },
    set: (val: Column[]) => {
      orderedMetrics.value = val.filter(({ isTreeColumn }) => !isTreeColumn);
    },
  });

  const orderedMetricsMovable = computed({
    get: () => orderedMetricsTree.value.filter((item) => item && !item.disabled),
    set: (val: Column[]) => {
      const locked = orderedMetricsTree.value.filter((item) => item && item.disabled);
      orderedMetricsTree.value = [...locked, ...val];
    },
  });

  const selectedLength = computed(() => orderedMetricsTree.value.filter((item) => item).length);

  const filteredMetricsGroupedToSend = computed(() => {
    return filteredMetricsGrouped.value.map((groupInstance) => {
      const { name, id: groupId, isOpened } = groupInstance.group;
      return {
        groupId,
        name,
        isOpened,
        columns: groupInstance.columns.map((item) => item.value),
      };
    });
  });

  const hasNewColumnBtn = computed(() => {
    return ['campaigns', 'offers', 'landings', 'flows', 'traffic-sources', 'affiliate-networks'].includes(
      route.params.type as string,
    );
  });

  const prepareOrderMetrics = ({ isPrepareForReset }: { isPrepareForReset: boolean }) => {
    const newColumns: Column[] = [];
    let oldColumns = isPrepareForReset ? [] : [...orderedMetrics.value];

    selectedMetrics.value.forEach((value, index) => {
      const metric = existingMetrics.value.find((metricItem) => metricItem.index === index);
      if (value && !selectedMetricsPrevState.value[index]) {
        newColumns.push(metric);
      }

      if (!value && selectedMetricsPrevState.value[index]) {
        oldColumns = oldColumns.filter((item) => item.value !== metric.value);
      }
    });
    selectedMetricsPrevState.value = [...selectedMetrics.value];
    return [...oldColumns, ...newColumns];
  };

  const getCurrentSelectedMetricsHash = () => {
    return md5(JSON.stringify(orderedMetricsTree.value.map((item) => item.value)));
  };

  const getCurrentGroupOfMetricsHash = () => {
    return md5(JSON.stringify(filteredMetricsGroupedToSend.value));
  };

  const checkSelectedAll = () => {
    const newSelectAll = selectedLength.value === getAllExistingMetricsWithoutLimits.value.length;

    if (selectAll.value !== newSelectAll) {
      selectAll.value = newSelectAll;
    }
  };

  const recalculateSelectedInGroup = ({ item, groupIndex }: { item?: Column; groupIndex?: number } = {}) => {
    if (pagesWithoutGrouping.value) return;

    if (item && typeof groupIndex !== 'undefined') {
      const newValue = filteredMetricsGrouped.value[groupIndex].group.selected + (selectedMetrics.value[item.index] ? 1 : -1);
      filteredMetricsGrouped.value[groupIndex].group.selected = newValue;
      return;
    }

    if (!item && typeof groupIndex !== 'undefined') {
      const groupInstance = filteredMetricsGrouped.value[groupIndex];
      let selected = 0;

      const updateSelected = (column: Column) => {
        if (selectedMetrics.value[column.index]) {
          selected = selected + 1;
        }
      };
      groupInstance.columns.forEach((column) => updateSelected(column));
      filteredMetricsGrouped.value.splice(groupIndex, 1, {
        ...groupInstance,
        group: {
          ...groupInstance.group,
          selected,
        },
      });
      return;
    }

    filteredMetricsGrouped.value = filteredMetricsGrouped.value.map((groupInstance) => {
      let selected = 0;

      const updateSelected = (column: Column) => {
        if (selectedMetrics.value[column.index]) {
          selected = selected + 1;
        }
      };

      groupInstance.columns.forEach((column) => updateSelected(column));
      return {
        ...groupInstance,
        group: {
          ...groupInstance.group,
          selected,
        },
      };
    });
  };

  const calculateFilteredMetricsGrouped = () => {
    if (pagesWithoutGrouping.value) return;
    const getDefaultGroup = (column: Column) => (
      column.isCustomColumn || column.value.startsWith('reports-customevent') ? 'custom_columns' : 'general'
    );

    const filteredMetricsGroupObject = filteredMetrics.value.reduce((acc, item) => {
      const groupIdFromServer = groupingFromServer.value.columns?.[item.value]?.groupId as string | undefined;
      const columnSortIndexFromServer = groupingFromServer.value.columns?.[item.value]?.index as number | undefined;

      const groupId = groupIdFromServer || getDefaultGroup(item);
      if (!acc[groupId]) {
        acc[groupId] = { columnWithSavedIndex: [], columnWithoutSavedIndex: [], disabledColumns: [] };
      }

      if (item.disabled) {
        if (!acc['general']) {
          acc['general'] = { columnWithSavedIndex: [], columnWithoutSavedIndex: [], disabledColumns: [] };
        }
        acc['general'].disabledColumns.push(item);
      }
      if (typeof columnSortIndexFromServer === 'undefined') {
        acc[groupId].columnWithoutSavedIndex.push(item);
      } else {
        acc[groupId].columnWithSavedIndex[columnSortIndexFromServer] = item;
      }
      return acc;
    }, {} as {
      [type: string]: {
        columnWithSavedIndex: Column[];
        columnWithoutSavedIndex: Column[];
        disabledColumns: Column[];
      };
    });

    Object.keys(reportParamsApi.savedGroups.value.groups).forEach((groupId) => {
      if (!filteredMetricsGroupObject[groupId]) {
        filteredMetricsGroupObject[groupId] = {
          columnWithSavedIndex: [],
          columnWithoutSavedIndex: [],
          disabledColumns: [],
        };
      }
    });

    const groupsWithIndexArray: ColumnsGroup[] = [];
    const groupsWithoutIndexArray: ColumnsGroup[] = [];
    const reCreatedGeneralGroup: ColumnsGroup[] = [];
    Object.keys(filteredMetricsGroupObject).forEach((groupKey) => {
      const isOpened = groupingFromServer.value.groups?.[groupKey]?.isOpened as boolean | undefined;
      const groupInstance = {
        group: {
          ...groupingFromServer.value.groups?.[groupKey],
          id: groupKey,
          isOpened: typeof isOpened === 'undefined' ? true : isOpened,
        },
        disabledColumns: filteredMetricsGroupObject[groupKey].disabledColumns,
        columns: [
          ...filteredMetricsGroupObject[groupKey].columnWithSavedIndex,
          ...filteredMetricsGroupObject[groupKey].columnWithoutSavedIndex,
        ].filter((item) => item),
      } as ColumnsGroup;
      const groupIndexFromServer = groupingFromServer.value?.groups?.[groupKey]?.index as number | undefined;

      if (groupKey === 'general' && typeof groupIndexFromServer === 'undefined') {
        reCreatedGeneralGroup[0] = groupInstance;
        return;
      }
      if (typeof groupIndexFromServer !== 'undefined') {
        groupsWithIndexArray[groupIndexFromServer] = groupInstance;
        return;
      }

      groupsWithoutIndexArray.push(groupInstance);
    });

    filteredMetricsGrouped.value = [
      ...reCreatedGeneralGroup,
      ...groupsWithIndexArray.filter((item) => item),
      ...groupsWithoutIndexArray,
    ];
  };

  const init = () => {
    search.value = '';
    selectedMetrics.value = initSelectedMetrics.value;
    selectedMetricsPrevState.value = [...selectedMetrics.value];

    groupingFromServer.value = reportParamsApi.savedGroups.value;

    orderedMetrics.value = savedColumns.value
      .map((key: string) => filteredMetrics.value.find(({ value }) => value === key))
      .filter((item: Column | undefined) => item) as Column[];

    checkSelectedAll();

    oldSelectedMetricsHash.value = getCurrentSelectedMetricsHash();

    calculateFilteredMetricsGrouped();
    recalculateSelectedInGroup();
    oldGroupOfMetricsHash.value = getCurrentGroupOfMetricsHash();
  };

  const selectedMetricsChanged = ({ item, groupIndex }: { item?: Column; groupIndex?: number } = {}) => {
    orderedMetrics.value = prepareOrderMetrics({ isPrepareForReset: false });
    checkSelectedAll();
    recalculateSelectedInGroup({ item, groupIndex });
  };

  const columnHandleDragEnd = () => {
    recalculateSelectedInGroup();
  };

  const closeMenuPreset = ({ ref: refName, index }: { ref?: string; index?: number | string }) => {
    if (refName) {
      const buttonRef = newPreset.value;
      buttonRef?.toggleMenu();
    }
    if (typeof index !== 'undefined') {
      presetsRefs.value[index as number]?.toggleMenu();
    }
  };

  const close = () => {
    emit('close');

    if (isActivePresetWasDropedToGeneral.value) {
      store.replaceTableColumnsFiltersForCurrentTab({});
      $emitter.emit('update-table-filters');
      $emitter.emit('reload-report');
    }
    if (isActivePresetWasUpdated.value) {
      $emitter.emit('reload-report');
    }
  };

  const checkClose = () => {
    if (oldSelectedMetricsHash.value !== getCurrentSelectedMetricsHash()) {
      $emitter.emit('open-dialog', { name: 'ConfirmDialog', data: { onConfirm: () => close() } });
    } else {
      close();
    }
  };

  const savePreset = async ({
    ref: refName,
    index,
    presetId,
  }: {
    ref?: string;
    index?: string | number;
    presetId?: string;
  }) => {
    clearErrors();
    if (presetId && inputPresetName.value === reportParamsApi.activePresetData.value?.presetName) {
      closeMenuPreset({ index, ref: refName });
      return;
    }
    const presetNameValue = presetId ? inputPresetName.value : inputPresetNameNew.value;
    presetSaveLoading.value = true;
    let newPresetId: string | null = null;
    if (!presetId) {
      newPresetId = uuidv4();
    }
    const presetIdFinal = presetId || newPresetId;
    const presetData = presetId
      ? reportParamsApi.savedColumns.value[activePreset.value]
      : DEFAULT_COLUMN_PRESET_STATE;

    try {
      const presetDataServerFormat = (Object.values(presetData) as ColumnsSettingsContext[]).reduce(
        (acc, item) => {
          const { context, order, columns, width } = item;

          acc.push({
            context,
            order,
            columns: columns.map((column) => ({
              name: column,
              width: width[column],
            })),
          });
          return acc;
        },
        [] as ColumnsSettingsContextServerFormat[],
      );

      await putColumnSettingsApiRequest({
        id: presetIdFinal,
        name: presetNameValue,
        settings: presetDataServerFormat,
      });

      if (!presetId) {
        activePreset.value = newPresetId;
        inputPresetName.value = presetNameValue;
      }

      store.updatePreset({ presetId: presetIdFinal, presetData });

      const presetsInfo = [...reportParamsApi.presetsInfo.value];
      if (presetId) {
        presetsInfo[presetsInfo.findIndex((item) => item.presetId === presetIdFinal)].presetName = presetNameValue;
      } else {
        presetsInfo.push({
          presetId: newPresetId,
          presetName: presetNameValue,
        });
      }
      store.setPresetsInfo(presetsInfo);
      closeMenuPreset({ index, ref: refName });
      init();

      $emitter.emit(
        'toast-success',
        presetId
          ? t('Preset name has been changed')
          : t('Preset has been saved'),
      );
    } catch (error) {
      applyApiErrors(error, { nested: true });
    } finally {
      presetSaveLoading.value = false;
    }
  };

  const deletePreset = ({ presetId, index }: { presetId: string; index: number | string }) => {
    presetDeleteLoading.value = true;
    deleteColumnSettingsPresetApiRequest(presetId).finally(() => {
      store.deletePreset({ presetId });
      if (reportParamsApi.activePreset.value === presetId) {
        store.setActivePreset(GENERAL_PRESET);
        isActivePresetWasDropedToGeneral.value = true;
      }
      activePreset.value = GENERAL_PRESET;
      init();

      const presetsInfo = [...reportParamsApi.presetsInfo.value];
      presetsInfo.splice(presetsInfo.findIndex((item) => item.presetId === presetId), 1);
      store.setPresetsInfo(presetsInfo);

      presetDeleteLoading.value = false;
      closeMenuPreset({ index });

      $emitter.emit('toast-success', t('Preset has been deleted'));
    });
  };

  const openDeletePresetDialog = ({ presetId, index }: { presetId: string; index: number | string }) => {
    closeMenuPreset({ index });
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: {
        message: t('Selected preset will be deleted if you press OK.'),
        onConfirm: () => deletePreset({ presetId, index }),
      },
    });
  };

  const clickNewPreset = () => {
    inputPresetNameNew.value = '';
    clearErrors();
    const openMenu = () => {
      newPreset.value?.toggleMenu();
    };

    if (oldSelectedMetricsHash.value === getCurrentSelectedMetricsHash()) {
      openMenu();
    } else {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: {
          title: t('Save changes'),
          message: t('Press OK to save the changes to this preset and move to the settings for the other preset.'),
          onConfirm: () => saveColumns(false, true).then(openMenu),
        },
      });
    }
  };

  const selectPreset = ({ presetId, presetName }: { presetId: string; presetName: string }) => {
    clearErrors();
    if (presetId === activePreset.value) return;
    const switchPreset = () => {
      inputPresetName.value = presetName;
      activePreset.value = presetId;
      init();
    };
    if (oldSelectedMetricsHash.value === getCurrentSelectedMetricsHash()) {
      switchPreset();
    } else {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: {
          title: t('Save changes'),
          message: t('Press OK to save the changes to this preset and move to the settings for the other preset.'),
          onConfirm: () => saveColumns(false, true).then(switchPreset),
        },
      });
    }
  };

  const getAllMetricsWithoutLimitsInGroup = (groupData: ColumnsGroup) => {
    return groupData.columns.filter((item) => !hasPlanLimitsRestriction(item.planLimits));
  };

  const isGroupSelected = (groupData: ColumnsGroup) => {
    const allMetricsWithoutLimitsInGroup = getAllMetricsWithoutLimitsInGroup(groupData).length;
    if (groupData.group.selected === 0) return false;

    return groupData.group.selected >= allMetricsWithoutLimitsInGroup;
  };

  const isDisplayColumn = (item: Column) => {
    return !(isTreeView.value && item.export);
  };

  const getHelp = (item: Column, helpFull = false) => {
    return helpFull ? item.helpFull : item.help;
  };

  const handleSelectAllChanged = (value: boolean) => {
    const groupBy = reportGroupBy.value;
    const { dimensions } = store;
    const types = (Array.isArray(groupBy) ? groupBy : [groupBy])?.map((key) => dimensions[key]?.mainColumn);

    selectedMetrics.value = existingMetrics.value.reduce((acc: boolean[], item: Column) => {
      if (hasPlanLimitsRestriction(getReportColumnPlanLimits(item))) {
        acc.push(false);
      } else {
        acc.push(item.disabled || types?.includes(item.value) ? true : value);
      }

      return acc;
    }, []);

    selectedMetricsChanged();
  };

  const handleSelectAllInGroup = (groupIndex: number) => {
    const groupData = filteredMetricsGrouped.value[groupIndex];
    const newColumnValue = groupData.group.selected !== getAllMetricsWithoutLimitsInGroup(groupData).length;

    const newSelectedMetrics = [...selectedMetrics.value];
    filteredMetricsGrouped.value[groupIndex].columns.forEach((column) => {
      if (column.disabled) {
        newSelectedMetrics[column.index] = true;
        return;
      }

      newSelectedMetrics[column.index] = hasPlanLimitsRestriction(column.planLimits) ? false : newColumnValue;
    });
    selectedMetrics.value = newSelectedMetrics;
    selectedMetricsChanged({ groupIndex });
  };

  const handleDragStart = (event: { item: { dataset: { value: string } } }) => {
    dragged.value = event.item.dataset.value;
  };

  const handleDragEnd = () => {
    dragged.value = null;
  };

  const onAddNewColumn = (item: Column | null, groupIndex: number | null) => {
    $emitter.emit('open-dialog', {
      name: 'AddNewColumnDialog',
      data: { item, groupIndex, inheritMetrics: existingMetrics.value },
    });
  };

  const onDelete = (item: Column, groupIndex: number) => {
    const { id, index, value } = item;
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: {
        message: t('All selected items will be deleted permanently if you press OK.'),
        onConfirm: () => {
          deleteCustomColumnApiRequest(id).then(() => {
            Promise.all([refreshSupportedMetricsApiRequest(), refreshCustomColumns()]).then(() => {
              const newColumnsValue = [...filteredMetricsGrouped.value[groupIndex].columns];
              const columnIndexToRemove = newColumnsValue.findIndex((column) => column?.id === item.id);
              newColumnsValue.splice(columnIndexToRemove, 1);
              filteredMetricsGrouped.value[groupIndex].columns = newColumnsValue;

              const columnIndexInOrdered = orderedMetrics.value.findIndex((metric) => metric.value === item.value);
              if (selectedMetrics.value[item.index]) {
                orderedMetrics.value.splice(columnIndexInOrdered, 1);
              }
              selectedMetrics.value.splice(index, 1);
              selectedMetricsPrevState.value = [...selectedMetrics.value];

              $emitter.emit('update-custom-columns', {
                item,
                action: 'remove',
                groupIndex,
              });
              store.omitReportsColumns([value]);
            });

            $emitter.emit('toast-success', t('The custom column will be permanently deleted with all its calculated data.'));
          });
        },
      },
    });
  };

  const resetDefaults = () => {
    selectedMetricsPrevState.value = [];
    selectedMetrics.value = getDefaultSelected().reduce((acc: boolean[], { index }: Column) => {
      acc[index] = true;
      return acc;
    }, []);

    orderedMetrics.value = prepareOrderMetrics({ isPrepareForReset: true });

    checkSelectedAll();
    recalculateSelectedInGroup();
  };

  const getResetResizeTitle = (inheritColumns: boolean) => {
    return inheritColumns ? t('Reset columns width for this section') : t('Reset columns width for all sections');
  };

  const resetResize = (alternative: boolean) => {
    const inheritColumns = getInheritColumns(alternative);

    const dimensions = tableFullDimensions();
    const groupBy = reportGroupBy.value;

    const contexts = inheritColumns ? Object.keys(allSavedColumns.value) : [groupBy];

    const payload = contexts.map((context) => {
      const { mainColumn } = dimensions[context] || {};
      const { width, order } = allSavedColumns.value[context];

      const resetKeys = Object.keys(width);

      const columnsWidth = resetKeys.map((key) => ({
        name: key,
        width: key === mainColumn ? 300 : 100,
      }));

      return {
        columns: columnsWidth,
        context,
        order,
      };
    }) as ColumnsSettingsContextServerFormat[];

    putColumnSettingsApiRequest({
      id: activePreset.value,
      name: inputPresetName.value,
      settings: payload,
    }).finally(() => {
      store.patchSavedColumnsFromServerFormat({
        presetId: activePreset.value,
        presetDataServerFormat: payload,
      });
      $emitter.emit('reset-table-width');
      $emitter.emit('toast-success', t('Column width reset successfully'));
    });
  };

  const setIsOpenedGroup = (key: string) => {
    const groupOfColumns = filteredMetricsGrouped.value.find((item) => item.group.id === key);
    if (!groupOfColumns) return;
    groupOfColumns.group.isOpened = !groupOfColumns.group.isOpened;
  };

  const getTableMetrics = (type: string) => {
    return resolveDedicatedTableMetrics(type, store.metrics as string[]);
  };

  const sortColumns = (columns: string[], mainColumn: string, currentMainColumn: string) => {
    const orderedMetricsValues = orderedMetricsTree.value.map(({ value }) => value);
    const valueWithoutMainColumn = orderedMetricsValues.filter((columnName: string) => columnName !== currentMainColumn);

    const sortIndex = columns.reduce((acc: Record<string, number>, columnName, index) => {
      acc[columnName] = valueWithoutMainColumn.findIndex((value) => value === columnName);
      if (acc[columnName] === -1 && columnName !== mainColumn) {
        acc[columnName] = index;
      }

      return acc;
    }, {});

    return columns.sort((a, b) => {
      const aScore = sortIndex[a];
      const bScore = sortIndex[b];

      if (aScore > bScore) {
        return 1;
      }
      if (aScore < bScore) {
        return -1;
      }
      return 0;
    });
  };

  const allAllowedMetricsOfDimension = (dimension: string) => {
    const dimensions = store.dimensions as Record<string, { mainColumn?: string; additionalColumns?: string[] }>;
    const query = tableQueryApi.getTableQueryForCurrentTab() as { treeLevels?: string[] };
    const treeLevelColumns = (query.treeLevels || [])
      .filter((item) => !!item)
      .map((item) => dimensions[item]?.mainColumn)
      .filter((value): value is string => Boolean(value));
    const reportExclude = (baseMetrics.value as { report_exclude?: Record<string, string[]> } | undefined)?.report_exclude;
    const hideFilters = typeof (store as { getIsHideFilters?: () => boolean }).getIsHideFilters === 'function'
      && (store as { getIsHideFilters: () => boolean }).getIsHideFilters();

    return resolveAllowedMetricsForDimension(dimension, {
      getDedicatedColumns: (routeName) => {
        const metrics = getCustomMetrics(routeName) as {
          avaliable_metrics?: string[];
          default_include?: string[];
        } | undefined;

        return listDedicatedMetricSaveFlags({
          availableMetrics: metrics?.avaliable_metrics || [],
          defaultInclude: metrics?.default_include || [],
        });
      },
      getSimpleColumns: (dimensionTypeName) => {
        const dimensionMeta = dimensions[dimensionTypeName];
        if (!dimensionMeta?.mainColumn) {
          return [];
        }

        return listSimpleMetricSaveFlags({
          additionalColumns: dimensionMeta.additionalColumns || [],
          metrics: (store.metrics as string[]) || [],
          mainColumn: dimensionMeta.mainColumn,
          treeLevelColumns,
          exclude: hideFilters ? reportExclude?.[dimensionTypeName] : undefined,
        });
      },
    });
  };

  const saveColumns = (inheritColumns: boolean, dontCloseModal?: boolean) => {
    store.setLoading(true);
    let newTableFilters;

    const groupBy = reportGroupBy.value;
    const contexts = inheritColumns
      ? Object.keys(allSavedColumns.value).filter((key) => !['live-visits', 'live-clicks'].includes(key))
      : [groupBy];

    const tableDimensions = tableFullDimensions();
    const type = reportTypeFromRoute();

    const tableMetrics = getTableMetrics(type);

    const orderedMetricsValues = orderedMetrics.value.map(({ value }) => value);
    const currentTableMetrics = orderedMetricsValues.filter((columnName: string) => tableMetrics.includes(columnName));
    const currentUniqueTableMetrics = orderedMetricsValues.filter((columnName: string) => !tableMetrics.includes(columnName));
    const currentCustomTableMetrics = orderedMetricsValues.filter((columnName: string) => columnName.includes('reports-customevent'));

    const {
      additionalColumns: currentAdditionalColumns,
      mainColumn: currentMainColumn,
    } = tableDimensions[type] || { mainColumn: null, additionalColumns: [] };

    const resultColumns = contexts.reduce(
      (acc, dimension: string) => {
        const { mainColumn, additionalColumns } = tableDimensions[dimension] || { mainColumn: null, additionalColumns: [] };

        const inheritingTableMetrics = allSavedColumns.value[dimension].columns;
        const sourceType = inheritColumns ? type : dimension;

        let result: string[] = inheritingTableMetrics;

        if (columnsSettingsDialog.isMetricAllowToInherit(inheritColumns, dimension, type)) {
          const commonAdditionalColumns = currentAdditionalColumns.filter((item: string) => additionalColumns.includes(item));
          const commonUniqueTableMetrics = currentUniqueTableMetrics.filter((columnName: string) => commonAdditionalColumns.includes(columnName));
          const allAllowedMetrics = allAllowedMetricsOfDimension(dimension);
          const allowedMetricValues = new Set(allAllowedMetrics.map((metric) => metric.value));
          const selectedAndDisabledAndNotTree = allAllowedMetrics
            .filter((item) => item?.disabled === true && !item.isTreeLevel)
            .map((item) => item.value);

          result = result
            .filter((item: string) =>
              !tableMetrics.includes(item)
              && !commonAdditionalColumns.includes(item)
              && !item.includes('reports-customevent')
              && allowedMetricValues.has(item),
            )
            .concat(selectedAndDisabledAndNotTree)
            .concat(commonUniqueTableMetrics)
            .concat(currentTableMetrics)
            .concat(currentCustomTableMetrics);

          result = _.uniqBy(result, (column) => column);

          result = sortColumns(result, mainColumn, currentMainColumn);
        }

        let columnsContext: ColumnsSettingsContextServerFormat = {};

        const notSortableColumns = getCustomMetrics(dimensionType(dimension))?.sort_exclude;
        const sourceTypeSort = inheritColumns ? type : dimension;
        if (allSavedColumns.value[sourceTypeSort]?.order) {
          let sort: ColumnsSort = allSavedColumns.value[sourceTypeSort].order;

          const currentSort = result.find((columnName) => columnName === sort.orderBy);

          if (!result.some((columnName) => columnName === sort.orderBy) || notSortableColumns.includes(currentSort)) {
            const oldSort: ColumnsSort = allSavedColumns.value[dimension]?.order;

            if (
              inheritColumns
              && oldSort
              && result.some((columnName) => columnName === oldSort.orderBy)
              && !notSortableColumns.includes(currentSort)
            ) {
              sort = oldSort;
            } else {
              sort = {
                orderBy: findOrderBy(result, dimension),
                orderDir: 'desc',
              };
            }
          }

          columnsContext.order = sort;
        }

        columnsContext = {
          ...columnsContext,
          context: dimension,
          columns: _.uniq(result).map((columnName: string) => {
            const width = _.get(allSavedColumns.value, [sourceType, 'width', columnName])
              || _.get(allSavedColumns.value, [dimension, 'width', columnName])
              || _.get(baseMetrics.value, ['metrics_options', columnName], columnName === mainColumn ? 300 : 100);
            const savedTotalType = allSavedColumns.value[dimension]?.totalType?.[columnName];
            return {
              name: columnName,
              width: Math.floor(width),
              ...(savedTotalType && { totalType: savedTotalType }),
            } as ColumnsSettings;
          }),
        };

        if (type === dimension && activePreset.value === reportParamsApi.activePreset.value) {
          const tableColumnsFilters = tableQueryApi.getTableColumnsFiltersForCurrentTab();
          const newTableFiltersValue = _.pick(tableColumnsFilters, result);
          if (!isEqual(newTableFiltersValue, tableColumnsFilters)) {
            newTableFilters = newTableFiltersValue;
          }
        }

        if (activeTab.value) {
          acc.local.push(columnsContext);
        }
        if (!activeTab.value || type === 'live-clicks' || type === 'live-visits') {
          if (columnsSettingsDialog.isMetricAllowToInherit(inheritColumns, dimension, type)) {
            const { columnsFront, widthFront, totalTypeFront } = columnsContext.columns.reduce(
              (reduceAcc, { name, width, totalType }) => {
                reduceAcc.widthFront[name] = width;
                reduceAcc.totalTypeFront[name] = totalType;
                reduceAcc.columnsFront.push(name);
                return reduceAcc;
              },
              { columnsFront: [] as string[], widthFront: {} as Record<string, number>, totalTypeFront: {} as Record<string, string> },
            );
            acc.front[dimension] = {
              ...columnsContext,
              columns: columnsFront,
              width: widthFront,
              totalType: totalTypeFront,
            };
            if (columnsSettingsDialog.allowToSaveThisMetricOnServer.value) {
              acc.server.push(columnsContext);
            }
          }
        }

        return acc;
      },
      {
        front: {
          ...DEFAULT_COLUMN_PRESET_STATE,
          ...allSavedColumns.value,
        },
        server: [] as ColumnsSettingsContextServerFormat[],
        local: [] as ColumnsSettingsContextServerFormat[],
      },
    );

    if (type === 'live-visits' || type === 'live-clicks' || !activeTab.value) {
      return putColumnSettingsApiRequest({
        id: activePreset.value,
        name: inputPresetName.value,
        settings: resultColumns.server,
      }).then(() => {
        if (type === 'live-visits' || type === 'live-clicks') {
          store.applyActiveReportColumnsFromContexts(resultColumns.local);
        }

        store.updatePreset({
          presetId: activePreset.value,
          presetData: resultColumns.front,
        });

        if (newTableFilters) {
          store.replaceTableColumnsFiltersForCurrentTab(newTableFilters);
          $emitter.emit('update-table-filters');
        }

        store.setLoading(false);

        if (!dontCloseModal) {
          close();
        }

        if (
          (activePreset.value === reportParamsApi.activePreset.value || isActivePresetWasUpdated.value || isActivePresetWasDropedToGeneral.value)
          && !dontCloseModal
        ) {
          $emitter.emit('reload-report');
        }

        if (activePreset.value === reportParamsApi.activePreset.value) {
          isActivePresetWasUpdated.value = true;
        }
      });
    }

    if (resultColumns.local.length) {
      store.applyActiveReportColumnsFromContexts(resultColumns.local);
      close();
      $emitter.emit('reload-report');
    }
  };

  const submit = (alternative = false) => {
    store.clearTableChartHiddenSeries();

    const inheritColumns = getInheritColumns(alternative);

    if (oldGroupOfMetricsHash.value !== getCurrentGroupOfMetricsHash()) {
      postColumnsGroupsSettingApiRequest(filteredMetricsGroupedToSend.value).then(() => {
        store.setGroupsColumns(filteredMetricsGroupedToSend.value);
        if (oldSelectedMetricsHash.value !== getCurrentSelectedMetricsHash()) {
          const inheritColumnsValue = getInheritColumns(alternative);
          saveColumns(inheritColumnsValue);
        }
      });
      close();
      return;
    }

    if (oldSelectedMetricsHash.value !== getCurrentSelectedMetricsHash()) {
      saveColumns(inheritColumns);
      close();
      return;
    }
    close();
  };

  const actions = computed(() => {
    const isSpecialDimensions = columnsSettingsDialog.isSpecialDimensions();
    return {
      newColumn: {
        key: 'newColumn',
        title: t('New Column'),
        icon: 'fas fas fa-plus',
        click: () => onAddNewColumn(null, null),
      },
      resetColumns: {
        key: 'resetColumns',
        title: t('Reset to defaults'),
        icon: 'fas fa-history',
        click: () => resetDefaults(),
      },
      resetResize: {
        key: 'resetResize',
        title: getResetResizeTitle(store.user.independentColumnSettingsEnabled),
        icon: 'fas fa-history',
        click: () => resetResize(false),
        showCondition: !isSpecialDimensions,
      },
      resetResizeAlternative: {
        key: 'resetResizeAlternative',
        title: getResetResizeTitle(!store.user.independentColumnSettingsEnabled),
        icon: 'fas fa-history',
        click: () => resetResize(true),
        showCondition: !isSpecialDimensions,
      },
      resetResizeComplex: {
        key: 'resetResizeAlternative',
        title: t('Reset columns width'),
        icon: 'fas fa-history',
        click: () => resetResize(false),
        showCondition: isSpecialDimensions,
      },
      apply: {
        key: 'apply',
        title: `${t('Apply')} ${selectedLength.value || ''}`,
        icon: 'fas fa-check',
        click: () => submit(),
        showCondition: !isSpecialDimensions,
      },
      applyAlternative: {
        key: 'applyAlternative',
        title: !store.user.independentColumnSettingsEnabled
          ? t('Apply to this section')
          : t('Apply to all sections'),
        icon: 'fas fa-check',
        click: () => submit(true),
        showCondition: !isSpecialDimensions,
      },
      applyComplex: {
        key: 'apply',
        title: `${t('Apply')} ${selectedLength.value || ''}`,
        icon: 'fas fa-check',
        click: () => saveColumns(false),
        showCondition: isSpecialDimensions,
      },
    };
  });

  const applyMenuList = computed((): Bemob.ListItem[] => {
    return [
      actions.value.apply,
      actions.value.applyAlternative,
      actions.value.applyComplex,
    ].filter((item) => !!item.showCondition);
  });

  const resetMenuList = computed((): Bemob.ListItem[] => {
    return [
      actions.value.resetColumns,
      ...resetResizeMenuList.value,
    ];
  });

  const resetResizeMenuList = computed((): Bemob.ListItem[] => {
    return [
      actions.value.resetResize,
      actions.value.resetResizeAlternative,
      actions.value.resetResizeComplex,
    ].filter((item) => !!item.showCondition);
  });

  const onUpdateCustomColumns = ({
    action,
    item,
    groupIndex,
  }: {
    action: string;
    item: Column;
    groupIndex: number;
  }) => {
    if (action === 'add') {
      const customColumnsGroup = filteredMetricsGrouped.value.find((groupInstance) => groupInstance.group.id === 'custom_columns');
      const column = existingMetrics.value.find((metric) => metric.id === item.id);
      selectedMetrics.value.splice(column.index, 0, false);
      selectedMetricsPrevState.value = [...selectedMetrics.value];
      customColumnsGroup.columns.push(column);
      customColumnsGroup.group.isOpened = true;
    }
    if (action === 'edit') {
      const column = existingMetrics.value.find((metric) => metric.id === item.id);
      const columnIndexToReplace = filteredMetricsGrouped.value[groupIndex].columns.findIndex((metric) => metric?.id === item.id);
      filteredMetricsGrouped.value[groupIndex].columns.splice(columnIndexToReplace, 1, column);
    }

    if (action === 'remove' || action === 'add') {
      const existingMetricsObject = existingMetrics.value.reduce((acc: Record<string, Column>, metric) => {
        acc[metric.value] = metric;
        return acc;
      }, {});
      filteredMetricsGrouped.value.forEach((group) => {
        group.columns.forEach((column) => {
          column.index = existingMetricsObject[column.value].index;
        });
      });

      recalculateSelectedInGroup();
    }
  };

  onMounted(() => {
    inputPresetName.value = reportParamsApi.activePresetData.value?.presetName;
    init();
    $emitter.on('update-custom-columns', onUpdateCustomColumns);
  });

  onBeforeUnmount(() => {
    $emitter.off('update-custom-columns', onUpdateCustomColumns);
  });

  return {
    store,
    reportParamsApi,
    presetsInfo: reportParamsApi.presetsInfo,
    t,
    tc,
    GENERAL_PRESET,
    errorsDict,
    activeTab,
    activePreset,
    search,
    selectedMetrics,
    filteredMetricsGrouped,
    orderedMetricsTree,
    orderedMetricsMovable,
    dragged,
    selectAll,
    inputPresetName,
    inputPresetNameNew,
    presetSaveLoading,
    presetDeleteLoading,
    canEditCustomColumns,
    pagesWithoutGrouping,
    existing_metrics: existingMetrics,
    filteredMetrics,
    selectedLength,
    groupingValuesValueList,
    reportMetricsName,
    hasNewColumnBtn,
    actions,
    applyMenuList,
    resetMenuList,
    resetResizeMenuList,
    presetsRefs,
    newPreset,
    listOfGroups,
    buttons,
    checkClose,
    selectPreset,
    clickNewPreset,
    savePreset,
    closeMenuPreset,
    openDeletePresetDialog,
    handleSelectAllChanged,
    handleSelectAllInGroup,
    isGroupSelected,
    groupingTranslation: columnsSettingsDialog.groupingTranslation,
    groupingTooltipTranslation: columnsSettingsDialog.groupingTooltipTranslation,
    setIsOpenedGroup,
    columnHandleDragEnd,
    onAddNewColumn,
    onDelete,
    getHelp,
    selectedMetricsChanged,
    isDisplayColumn,
    handleDragStart,
    handleDragEnd,
  };
};
